import { request } from "@/lib/auth";
import type { ClimateScenario, HazardType, PrecipitationSeason, TimeHorizon } from "@shared/scoring/types";
import type { DroughtDetail } from "@shared/drought-detail";
import {
  EAC_PRECIPITATION_SEASONS,
  isMainRainySeason,
  MAIN_RAINY_SEASON_MONTHS,
  precipitationSeasonDescription,
  PRECIPITATION_SEASON_ORDER,
} from "@shared/precipitation-seasons";

export type ResultStatus = "computed" | "pending" | "no_data" | "not_applicable";

export interface AssetHazardResult {
  assetId: string;
  hazardType: HazardType;
  horizon: TimeHorizon;
  /** Set only for a future horizon's row. Always undefined for `historical` rows. */
  scenario?: ClimateScenario;
  /** Set only on `changing_precipitation` rows, which come one per season for
   * every horizon/scenario — MAM and OND for an East African site, a single
   * main rainy season (DJF/JFM/FMA) for a Malawi site. Seasons are scored
   * separately and never combined (functional spec §4.2). Undefined for every
   * other hazard. */
  season?: PrecipitationSeason;
  status: ResultStatus;
  exposure?: 1 | 2 | 3 | 4;
  vulnerability?: 1 | 2 | 3 | 4;
  risk?: 1 | 2 | 3 | 4;
  /**
   * Real hazard indicator score (1-4), for every hazard (per season for
   * changing_precipitation). `status`
   * can still be "pending" even when this is set — e.g. no vulnerability
   * default exists for this asset's value chain stage + hazard, or (rare)
   * this hazard's own score is the out-of-band 0 case (see
   * shared/scoring/exposure.ts). Don't treat its presence alone as "risk is
   * computed" — check `status` for that.
   */
  hazardScore?: 1 | 2 | 3 | 4;
  /** Only set for a Water Stress row sourced from the "Business-as-usual"
   * pathway — see server/routes/results.routes.ts for the full comment. */
  scenarioCaveat?: string;
  /** The hazard's own raw physical indicator (not the 1-4 score) for this
   * row's horizon/scenario. See formatIndicatorValue below for display. */
  indicatorValue?: number;
  /** Changing Precipitation only: why a row has no score (the pipeline's own
   * note, e.g. the dry-site rule). Undefined otherwise. */
  statusNote?: string;
  /** Drought only: the three scores + supporting-sentence inputs (methodology 7.3). */
  droughtDetail?: DroughtDetail;
}

export function getResults() {
  return request<{ results: AssetHazardResult[] }>("/api/results");
}

/** Display labels for the 6 hazards — functional spec §4. */
export const HAZARD_LABELS: Record<HazardType, string> = {
  heat_stress: "Heat Stress",
  changing_precipitation: "Changing Precipitation",
  water_stress: "Water Stress",
  drought: "Drought",
  floods: "Floods",
  landslide: "Landslide",
};

/** Display labels for the 3 horizons. "Current" is the only client-visible
 * spelling of the internal `historical` value — never render "Historical". */
export const HORIZON_LABELS: Record<TimeHorizon, string> = {
  historical: "Current",
  "2030": "2030",
  "2050": "2050",
};

/** East Africa's two precipitation seasons, in display order. */
export const PRECIPITATION_SEASONS = EAC_PRECIPITATION_SEASONS;

/**
 * A precipitation summary line: MAM, OND, or "main" — every site scored on
 * its main rainy season only (Malawi: DJF, JFM or FMA, which differ by site)
 * shares one "main rainy season" line, so a portfolio mixing East African and
 * Malawi assets gets three precipitation lines and nothing is dropped or
 * mislabelled as MAM/OND. Seasons are still never combined: each row counts
 * on exactly one line.
 */
export type PrecipitationLine = "MAM" | "OND" | "main";

/**
 * One line of a portfolio summary (gauge, share bar, "% at high risk",
 * takeaway): a hazard, except Changing Precipitation, which counts as one
 * line per PrecipitationLine — its seasons must never be combined into one
 * number. Per-asset tables keep one hazard line and show that asset's
 * seasons on it instead (see getAssetHazardCells).
 */
export type HazardKey = Exclude<HazardType, "changing_precipitation"> | `changing_precipitation:${PrecipitationLine}`;

export function precipitationLineOf(season: PrecipitationSeason | undefined): PrecipitationLine {
  if (isMainRainySeason(season)) return "main";
  return season ?? "MAM";
}

export function hazardKeyOf(row: { hazardType: HazardType; season?: PrecipitationSeason }): HazardKey {
  if (row.hazardType === "changing_precipitation") return `changing_precipitation:${precipitationLineOf(row.season)}`;
  return row.hazardType;
}

/**
 * The summary lines for a list of hazards, in the same order. Without
 * `results`, precipitation is MAM + OND (unchanged). With `results`, the
 * precipitation lines follow the data: MAM + OND when any row is MAM/OND (or
 * there are no precipitation rows at all), plus a "main rainy season" line
 * when any row is a main rainy season. An East-Africa-only portfolio
 * therefore reads exactly as before; a Malawi-only one gets just the main line.
 */
export function hazardKeysFor(hazards: readonly HazardType[], results?: readonly AssetHazardResult[]): HazardKey[] {
  let lines: PrecipitationLine[] = [...PRECIPITATION_SEASONS];
  if (results) {
    const present = new Set(
      results.filter((r) => r.hazardType === "changing_precipitation").map((r) => precipitationLineOf(r.season)),
    );
    const hasEac = present.has("MAM") || present.has("OND");
    lines = [];
    if (hasEac || !present.has("main")) lines.push(...PRECIPITATION_SEASONS);
    if (present.has("main")) lines.push("main");
  }
  return hazards.flatMap((h): HazardKey[] =>
    h === "changing_precipitation" ? lines.map((l) => `changing_precipitation:${l}` as const) : [h],
  );
}

export function hazardTypeOfKey(key: HazardKey): HazardType {
  return key.startsWith("changing_precipitation") ? "changing_precipitation" : (key as HazardType);
}

/** Season tag text for a season code or a summary line: "MAM", "OND",
 * "Main season" (the summary line), "Main season Dec–Feb" (one site's row). */
export function seasonTagText(season: string): string {
  if (season === "main") return "Main season";
  if (isMainRainySeason(season)) return `Main season ${MAIN_RAINY_SEASON_MONTHS[season]}`;
  return season;
}

/** e.g. "Floods", "Changing Precipitation (MAM)", "Changing Precipitation
 * (main rainy season)"; `short` gives "Precipitation (MAM)" / "Precip. (main)"
 * for narrow label columns, where the long form would
 * truncate before the season. */
export function hazardKeyLabel(key: HazardKey, short = false): string {
  const [hazardType, line] = key.split(":") as [HazardType, PrecipitationLine | undefined];
  if (!line) return HAZARD_LABELS[hazardType];
  const name = short ? "Precipitation" : HAZARD_LABELS[hazardType];
  // short form must fit the 8rem label column of the share bars ("Precipitation (main season)" truncates)
  if (line === "main") return short ? "Precip. (main)" : `${name} (main rainy season)`;
  return `${name} (${line})`;
}

/**
 * Label for one asset's hazard line (per-asset tables, map popup): the
 * hazard name, except a site scored on its main rainy season only, which
 * reads e.g. "Precipitation (main rainy season, Dec–Feb)". A MAM + OND site
 * keeps the plain hazard name (its season tags sit beside each score).
 */
export function assetHazardLabel(hazardType: HazardType, cells: readonly { season?: PrecipitationSeason }[]): string {
  const seasons = cells.map((c) => c.season);
  if (hazardType === "changing_precipitation" && seasons.length === 1 && isMainRainySeason(seasons[0])) {
    return `Precipitation (${precipitationSeasonDescription(seasons[0])})`;
  }
  return HAZARD_LABELS[hazardType];
}

/** Label for one result row in a row-level list (e.g. the risk matrix, top
 * risks): hazardKeyLabel, but a main-rainy-season row names its months. */
export function resultRowLabel(row: { hazardType: HazardType; season?: PrecipitationSeason }): string {
  if (row.hazardType === "changing_precipitation" && isMainRainySeason(row.season)) {
    return `${HAZARD_LABELS.changing_precipitation} (${precipitationSeasonDescription(row.season)})`;
  }
  return hazardKeyLabel(hazardKeyOf(row));
}

/** This asset's results across all 6 hazards, for a per-asset breakdown (e.g. a map popup). */
export function getResultsForAsset(assetId: string, results: AssetHazardResult[]): AssetHazardResult[] {
  return results.filter((r) => r.assetId === assetId);
}

/**
 * Reduces an asset's per-hazard results to one representative value for map-
 * marker coloring: the worst (highest) computed risk across its hazards, or
 * null if every hazard for this asset is still pending. A display-only
 * aggregation choice for the map pin — not a scoring decision; the
 * underlying per-hazard risk values are untouched and still shown in full
 * in the popup breakdown.
 */
export function getWorstRiskForAsset(assetId: string, results: AssetHazardResult[]): 1 | 2 | 3 | 4 | null {
  const computedRisks = getResultsForAsset(assetId, results)
    .filter((r) => r.status === "computed" && r.risk !== undefined)
    .map((r) => r.risk!);

  if (computedRisks.length === 0) return null;
  return Math.max(...computedRisks) as 1 | 2 | 3 | 4;
}

/**
 * Reduces every asset's result for one hazard, within an already
 * horizon/scenario-sliced and already asset-filtered results array, to the
 * worst (highest) computed risk — the hazard-axis mirror of
 * getWorstRiskForAsset's asset-axis reduction, for the By Hazard view's
 * left-panel summary badge. null when no row for this hazard in the given
 * slice has status "computed".
 */
export function getWorstRiskForHazard(
  hazardType: HazardType,
  resultsForOneHorizon: AssetHazardResult[],
): 1 | 2 | 3 | 4 | null {
  const computedRisks = resultsForOneHorizon
    .filter((r) => r.hazardType === hazardType && r.status === "computed" && r.risk !== undefined)
    .map((r) => r.risk!);

  if (computedRisks.length === 0) return null;
  return Math.max(...computedRisks) as 1 | 2 | 3 | 4;
}

/**
 * Every row for one asset x hazard out of an already horizon/scenario-sliced
 * results array: one row for most hazards; for changing_precipitation, MAM
 * then OND (East Africa) or a single main-rainy-season row (Malawi).
 * Per-asset tables render all of them on one hazard line.
 */
export function getAssetHazardCells(
  assetId: string,
  hazardType: HazardType,
  resultsForOneHorizon: AssetHazardResult[],
): AssetHazardResult[] {
  return resultsForOneHorizon
    .filter((r) => r.assetId === assetId && r.hazardType === hazardType)
    .sort((a, b) => seasonRank(a.season) - seasonRank(b.season));
}

function seasonRank(season: PrecipitationSeason | undefined): number {
  return season === undefined ? -1 : PRECIPITATION_SEASON_ORDER.indexOf(season);
}

/** Highest computed risk among `cells`, or undefined if none is computed. */
export function worstComputedRisk(cells: AssetHazardResult[]): 1 | 2 | 3 | 4 | undefined {
  const risks = cells.filter((c) => c.status === "computed" && c.risk !== undefined).map((c) => c.risk!);
  return risks.length ? (Math.max(...risks) as 1 | 2 | 3 | 4) : undefined;
}

/**
 * The single row for one asset x hazard (for changing_precipitation, pass
 * `season` to pick one of its seasons), out of an already horizon/scenario
 * -sliced results array (i.e. the caller has already called
 * getResultsForHorizon — AssetHazardResult has multiple rows per
 * asset x hazard across all horizons/scenarios, so this would be ambiguous
 * over the raw unsliced array from getResults()).
 */
export function getAssetHazardCell(
  assetId: string,
  hazardType: HazardType,
  resultsForOneHorizon: AssetHazardResult[],
  season?: PrecipitationSeason,
): AssetHazardResult | undefined {
  return resultsForOneHorizon.find(
    (r) => r.assetId === assetId && r.hazardType === hazardType && (season === undefined || r.season === season),
  );
}

/** Highest computed risk among the rows of one summary line (see HazardKey). */
export function getWorstRiskForHazardKey(
  key: HazardKey,
  resultsForOneHorizon: AssetHazardResult[],
): 1 | 2 | 3 | 4 | null {
  return worstComputedRisk(resultsForOneHorizon.filter((r) => hazardKeyOf(r) === key)) ?? null;
}

/**
 * The vs-today risk delta for one asset x hazard: the active horizon's risk
 * minus historical's risk. Returns undefined ("not comparable") whenever
 * either side's row is missing or has no computed risk, rather than
 * producing NaN or silently reading a genuine data gap as "no change" — see
 * docs/superpowers/specs/2026-09-13-tab3-by-asset-view-design.md's Part 2.
 */
export function getRiskDelta(
  assetId: string,
  hazardType: HazardType,
  resultsForActiveHorizon: AssetHazardResult[],
  resultsForHistorical: AssetHazardResult[],
  season?: PrecipitationSeason,
): number | undefined {
  const current = getAssetHazardCell(assetId, hazardType, resultsForActiveHorizon, season);
  const historical = getAssetHazardCell(assetId, hazardType, resultsForHistorical, season);

  if (current?.risk === undefined || historical?.risk === undefined) return undefined;
  return current.risk - historical.risk;
}

/** Unit/decimal formatting per hazard, matching the brief's own worked
 * examples ("37°C TXx", drought's "22% of rainy seasons") — see the design spec's "Per-hazard
 * raw indicator column" table. */
const INDICATOR_FORMAT: Record<HazardType, { unit: string; decimals: number; prefix?: string; signed?: boolean }> = {
  heat_stress: { unit: "°C TXx", decimals: 1 },
  // % departure of the season's rainfall from its baseline
  changing_precipitation: { unit: "% vs normal", decimals: 1, signed: true },
  water_stress: { unit: "", decimals: 2 },
  drought: { unit: "% of rainy seasons", decimals: 0 },
  floods: { unit: "mm Rx5day", decimals: 0 },
  landslide: { unit: "mm Rx3day", decimals: 0 },
};

export function formatIndicatorValue(hazardType: HazardType, value: number | undefined): string | undefined {
  if (value === undefined) return undefined;
  const { unit, decimals, prefix = "", signed = false } = INDICATOR_FORMAT[hazardType];
  const sign = signed && value > 0 ? "+" : "";
  return `${prefix}${sign}${value.toFixed(decimals)}${unit}`;
}
